import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDown,
  faAngleUp,
  faClock,
  faInfo,
  faChevronLeft,
  faChevronUp,
  faChevronDown,
  faChevronRight,
  faEdit,
  faBars,
} from '@fortawesome/free-solid-svg-icons';
import ReactPaginate from 'react-paginate';
import { loadPersonalStrategies, actionTypes } from '../../../actions/data';
import i18n from '../../../i18n.js';
import './StrategyList.css';
import StrategyRow from './StrategyRow';
import StrategySelect from './StrategySelect';
import TickerSelection from '../TickerSelection/TickerSelection';
import { Spinner } from '../Components/Spinner';
import StrategyEditor from 'wt-react/dist/components/StrategyEditor/StrategyEditor';

library.add(
  faClock,
  faAngleDown,
  faAngleUp,
  faInfo,
  faChevronLeft,
  faChevronUp,
  faChevronDown,
  faChevronRight,
  faEdit,
  faBars
);

export const KEY_METRICS = ['periodicity'];

const StrategyList = () => {
  const [currPage, setCurrPage] = useState(0);
  const [savingNewStrategy, setSavingNewStrategy] = useState(false);
  const [savingNewStrategyValue, setSavingNewStrategyValue] = useState(null);
  const [numPerPage] = useState(5);

  const dispatch = useDispatch();
  const personalStrategies = useSelector(
    (state) => state.data.personalStrategies
  );
  const personalStrategiesLoading = useSelector(
    (state) => state.data.personalStrategiesLoading
  );
  const personalStrategiesError = useSelector(
    (state) => state.data.personalStrategiesError
  );
  const params = useSelector((state) => state.params);

  useEffect(() => {
    if (!personalStrategiesLoading && !Array.isArray(personalStrategies)) {
      dispatch(loadPersonalStrategies());
    }
  }, [dispatch, personalStrategies, personalStrategiesLoading]);

  const paginate = ({ selected }) => {
    setCurrPage(selected);
  };

  const strategies = personalStrategies;
  const min = currPage * numPerPage;
  const max = min + numPerPage;

  const setSavingStrategyInProgress = (value, isStrategySaving) => {
    const updatedStrategies = [value, ...personalStrategies];

    if (!isStrategySaving && value) {
      dispatch({
        type: actionTypes.LOAD_PERSONAL_STRATEGIES,
        value: updatedStrategies,
      });
    }

    setSavingNewStrategy(isStrategySaving);
    setSavingNewStrategyValue(value);
  };

  const hasStrategies = Array.isArray(strategies) && strategies.length > 0;
  if (personalStrategiesError) {
    return null;
  }
  if (savingNewStrategy || hasStrategies) {
    const strategiesToList = strategies.slice(min, max);
    const pageCount = Math.ceil(strategies.length / numPerPage);

    return (
      <div className="strategyBlendList">
        <div className="row">
          <div className="selectCopy col-sm-12">
            {i18n.t('create-portfolio')}
          </div>
          <div className="selectBenchmark col-sm-12">
            {i18n.t('default-benchmark-select')}
          </div>
        </div>
        <StrategySelect strategiesToList={strategies} />
        <TickerSelection />
        <div className="headers">
          <div className="menu title">
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            {i18n.t('name')}
          </div>
          <div className="name title"></div>
          <div className="last-updated title">{i18n.t('last-edited')}</div>
          <div className="statistics">
            {KEY_METRICS.map((metric) => (
              <div
                className={'stat-' + metric}
                key={'stat-' + metric}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div className="stat-heading">
                  <div className="title">{i18n.t('num-holdings')}</div>
                </div>
                <ReactTooltip
                  id={metric + '-desc'}
                  type="light"
                  className="tooltip"
                  effect="float"
                >
                  {i18n.t(metric + '-desc')}
                </ReactTooltip>
              </div>
            ))}
          </div>
        </div>

        {savingNewStrategy && (
          <StrategyRow
            personalStrategy={savingNewStrategyValue}
            key={1}
            striped={1}
            saveInProgress={true}
          />
        )}

        {strategiesToList.map((strategy, index) => {
          return (
            <StrategyRow
              personalStrategy={strategy}
              key={strategy.id}
              striped={index % 2 !== 0}
              saveInProgress={false}
            />
          );
        })}

        <div className="last-row">
          <div className="row-pagination">
            <ReactPaginate
              previousLabel={<FontAwesomeIcon icon="chevron-left" size="xs" />}
              nextLabel={<FontAwesomeIcon icon="chevron-right" size="xs" />}
              pageCount={pageCount}
              marginPagesDisplayed={pageCount}
              pageRangeDisplayed={pageCount}
              onPageChange={paginate}
              containerClassName={'pagination'}
              pageClassName={'page-item'}
              pageLinkClassName={'page-item'}
              previousClassName={'page-item'}
              previousLinkClassName={'pageLink'}
              nextClassName={'page-item'}
              nextLinkClassName={'pageLink'}
              activeClassName={'active'}
              disabledClassName={'disabled'}
            />
          </div>
          <div className="new-strategy">
            <StrategyEditor
              key={'new-strategy-editor-1'}
              type={'create'}
              buttonLabel={i18n.t('new-strategy')}
              showEditor={true}
              token={params.token}
              clientEmail={params.advisorEmail}
              dataspanApiUrl={`${process.env.REACT_APP_API_URL}`}
              checkTickersUrl={`${process.env.REACT_APP_DPD_API_URL}`}
              typeHeadPlaceholder={'Search By Ticker or Name'}
              savedStrategyCallBack={setSavingStrategyInProgress}
              showInfoModal={false}
              closeModalOnSave={true}
            />
          </div>
        </div>
      </div>
    );
  } else if (personalStrategies && personalStrategies.length === 0) {
    return (
      <div className="no-strategies">
        <div className="no-strategies-label">
          <h4>{i18n.t('no-strategies')}</h4>
        </div>
        <div className="new-strategy">
          <StrategyEditor
            key={'new-strategy-editor-1'}
            type={'create'}
            buttonLabel={i18n.t('new-strategy')}
            showEditor={true}
            token={params.token}
            clientEmail={params.advisorEmail}
            dataspanApiUrl={`${process.env.REACT_APP_API_URL}`}
            checkTickersUrl={`${process.env.REACT_APP_DPD_API_URL}`}
            typeHeadPlaceholder={'Search By Ticker or Name'}
            savedStrategyCallBack={setSavingStrategyInProgress}
            showInfoModal={true}
            closeModalOnSave={true}
          />
        </div>
      </div>
    );
  }
  return <Spinner />;
};

export default StrategyList;
